package com.karyadina.ui.screens.flashcards.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.background
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.Info
import androidx.compose.material3.*
import androidx.compose.material3.ripple
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeOut
import androidx.compose.animation.fadeIn
import androidx.compose.foundation.BorderStroke
import com.karyadina.model.FlashCard_Subject
import com.karyadina.model.Flashcard
import com.karyadina.model.Unit as StudyUnit
import com.karyadina.service.FlashcardService

import com.karyadina.ui.components.FormulaDisplayBox
import com.karyadina.ui.components.ImageThumbnail
import com.karyadina.ui.components.ImageViewerDialog
import com.karyadina.ui.theme.FlashcardColors
import com.karyadina.ui.theme.FlashcardIcons
import com.karyadina.ui.theme.FlashcardAnimations
import com.karyadina.ui.theme.FlashcardGestureHandlers
import com.karyadina.ui.theme.ElevationProfile
import com.karyadina.ui.theme.ElevationUtils.rippleEffect
import kotlinx.coroutines.launch

/**
 * Truncates text to a maximum number of words and adds ellipsis if truncated
 */
fun String.truncateToWords(maxWords: Int = 80): String {
    val words = this.split(Regex("\\s+"))
    return if (words.size > maxWords) {
        words.take(maxWords).joinToString(" ") + "..."
    } else {
        this
    }
}

@Composable
fun QuickReviewCard(cardsToReview: Int, onClick: () -> Unit) {
    val isDarkMode = isSystemInDarkTheme()
    val cardGradient = Brush.linearGradient(
        colors = listOf(
            MaterialTheme.colorScheme.secondaryContainer,
            MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 1.0f)
        )
    )
    
    ElevatedCard(
        onClick = onClick,
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(cardGradient)
            .rippleEffect(bounded = true),
        colors = CardDefaults.elevatedCardColors(
            containerColor = Color.Transparent
        ),
        elevation = CardDefaults.elevatedCardElevation(
            defaultElevation = ElevationProfile.raisedElevation
        )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(20.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Surface(
                    modifier = Modifier.size(56.dp),
                    shape = CircleShape,
                    color = MaterialTheme.colorScheme.secondary
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            Icons.Filled.PlayArrow,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSecondary,
                            modifier = Modifier.size(32.dp)
                        )
                    }
                }
                
                Column {
                    Text(
                        "Quick Review",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        "$cardsToReview cards due",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSecondaryContainer.copy(alpha = 0.7f)
                    )
                }
            }
            
            Icon(
                Icons.Filled.ChevronRight,
                contentDescription = null,
                modifier = Modifier.size(32.dp)
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ReviewScreen(

    cards: List<Flashcard>,
    subject: FlashCard_Subject,
    unit: StudyUnit,
    service: FlashcardService,
    onComplete: () -> Unit
) {
    // Queue maintenance for "Review until Correct"
    var reviewQueue by remember { mutableStateOf(cards) }
    var completedCount by remember { mutableStateOf(0) }
    val totalInitialCards = remember { cards.size }
    
    var showAnswer by remember { mutableStateOf(false) }
    var expandedQuestion by remember { mutableStateOf(false) }
    var sessionId by remember { mutableStateOf("") }
    var showImageViewer by remember { mutableStateOf(false) }
    var selectedImageIndex by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()
    
    // Success celebration animation state
    val successState = remember { FlashcardAnimations.SuccessFeedbackState() }
    var showSuccessAnimation by remember { mutableStateOf(false) }
    
    LaunchedEffect(Unit) {
        val session = service.startReviewSession(subject.id, unit.id)
        sessionId = session.id
    }
    
    if (reviewQueue.isEmpty()) {
        // Review complete
        LaunchedEffect(Unit) {
            service.endReviewSession(sessionId)
        }
        
        ReviewCompleteScreen(
            totalCards = totalInitialCards,
            onFinish = onComplete
        )
    } else {
        val currentCard = reviewQueue.first()
        
        // Card flip animation
        val rotationProgress by animateFloatAsState(
            targetValue = if (showAnswer) 1f else 0f,
            animationSpec = tween(durationMillis = 400),
            label = "cardFlip"
        )
        val cardRotationY = FlashcardAnimations.getFlipRotation(rotationProgress)
        val questionOpacity = FlashcardAnimations.getFlipOpacity(rotationProgress, false)
        val answerOpacity = FlashcardAnimations.getFlipOpacity(rotationProgress, true)
        
        Scaffold(
            topBar = {
                TopAppBar(
                    title = {
                        Column {
                            Text("Review")
                            Text(
                                "Remaining: ${reviewQueue.size}",
                                style = MaterialTheme.typography.bodySmall
                            )
                        }
                    },
                    navigationIcon = {
                        IconButton(onClick = {
                            service.endReviewSession(sessionId)
                            onComplete()
                        }) {
                            Icon(Icons.Default.Close, "Exit")
                        }
                    }
                )
            }
        ) { padding ->
            val isDarkModeSetting = isSystemInDarkTheme()
            val questionContainerColor = if (isDarkModeSetting) {
                MaterialTheme.colorScheme.primary.copy(alpha = 0.2f)
            } else {
                MaterialTheme.colorScheme.primaryContainer
            }
            val answerContainerColor = if (isDarkModeSetting) {
                MaterialTheme.colorScheme.tertiary.copy(alpha = 0.2f)
            } else {
                MaterialTheme.colorScheme.tertiaryContainer
            }
            
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
                    .padding(12.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                // Progress
                LinearProgressIndicator(
                    progress = {if (totalInitialCards > 0) completedCount.toFloat() / totalInitialCards else 0f},
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(8.dp)
                        .clip(RoundedCornerShape(4.dp))
                )
                
                Spacer(modifier = Modifier.height(8.dp))
                
                // Card - Refactored for better content display
                ElevatedCard(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .rippleEffect(bounded = true),
                    colors = CardDefaults.elevatedCardColors(
                        containerColor = if (showAnswer) 
                            answerContainerColor
                        else 
                            questionContainerColor
                    ),
                    elevation = CardDefaults.elevatedCardElevation(
                        defaultElevation = ElevationProfile.raisedElevation
                    )
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(12.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Content area with dynamic height based on state
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .weight(1f)
                                .clickable { showAnswer = !showAnswer },
                            contentAlignment = Alignment.TopCenter
                        ) {
                            // Question side
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier
                                    .fillMaxSize()
                                    .verticalScroll(rememberScrollState())
                                    .graphicsLayer { alpha = questionOpacity }
                                    .padding(horizontal = 8.dp, vertical = 6.dp)
                            ) {
                                if (!showAnswer) {
                                    Icon(
                                        Icons.Filled.QuestionMark,
                                        contentDescription = null,
                                        modifier = Modifier.size(40.dp),
                                        tint = MaterialTheme.colorScheme.primary
                                    )
                                    
                                    Text(
                                        currentCard.question,
                                        style = MaterialTheme.typography.titleLarge,
                                        textAlign = TextAlign.Center,
                                        fontWeight = FontWeight.Bold
                                    )
                                    
                                    if (currentCard.hint != null) {
                                        Surface(
                                            shape = RoundedCornerShape(8.dp),
                                            color = if (isDarkModeSetting) {
                                                MaterialTheme.colorScheme.secondary.copy(alpha = 0.25f)
                                            } else {
                                                MaterialTheme.colorScheme.secondaryContainer
                                            },
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .padding(horizontal = 4.dp)
                                        ) {
                                            Row(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .padding(12.dp),
                                                verticalAlignment = Alignment.CenterVertically,
                                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                                            ) {
                                                Icon(
                                                    Icons.Filled.Info,
                                                    contentDescription = null,
                                                    modifier = Modifier.size(20.dp),
                                                    tint = if (isDarkModeSetting) {
                                                        Color.White
                                                    } else {
                                                        MaterialTheme.colorScheme.onSecondaryContainer
                                                    }
                                                )
                                                Text(
                                                    currentCard.hint!!.truncateToWords(80),
                                                    style = MaterialTheme.typography.bodySmall,
                                                    fontWeight = FontWeight.SemiBold,
                                                    color = if (isDarkModeSetting) {
                                                        Color.White
                                                    } else {
                                                        MaterialTheme.colorScheme.onSecondaryContainer
                                                    }
                                                )
                                            }
                                        }
                                    }
                                    
                                    Text(
                                        "Tap card to reveal answer",
                                        style = MaterialTheme.typography.labelSmall,
                                        color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.8f),
                                        textAlign = TextAlign.Center
                                    )
                                }
                            }
                            
                            // Answer side
                            if (showAnswer) {
                                Column(
                                    horizontalAlignment = Alignment.CenterHorizontally,
                                    verticalArrangement = Arrangement.Top,
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .verticalScroll(rememberScrollState())
                                        .graphicsLayer { 
                                            alpha = answerOpacity
                                        }
                                        .padding(horizontal = 8.dp, vertical = 6.dp)
                                ) {
                                    Spacer(modifier = Modifier.height(8.dp))
                                    Text(
                                        currentCard.answer,
                                        style = MaterialTheme.typography.bodyLarge,
                                        fontWeight = FontWeight.SemiBold,
                                        color = MaterialTheme.colorScheme.onTertiaryContainer,
                                        textAlign = TextAlign.Center
                                    )

                                    if (currentCard.hasFormula && currentCard.formulaLatex != null) {
                                        Spacer(modifier = Modifier.height(16.dp))
                                        FormulaDisplayBox(
                                            formulaImagePath = currentCard.formulaImagePath,
                                            formulaLatex = currentCard.formulaLatex,
                                            formulaDescription = currentCard.formulaDescription,
                                            formulaVariables = currentCard.formulaVariables
                                        )
                                    }

                                    val displayImages = currentCard.imagePaths.takeIf { it.isNotEmpty() }
                                        ?: currentCard.imagePath?.let { listOf(it) }
                                        ?: emptyList()

                                    if (displayImages.isNotEmpty()) {
                                        Spacer(modifier = Modifier.height(16.dp))
                                        LazyRow(
                                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            items(displayImages.size) { index ->
                                                ImageThumbnail(
                                                    imagePath = displayImages[index],
                                                    modifier = Modifier.size(100.dp),
                                                    showRemoveButton = false,
                                                    onView = {
                                                        selectedImageIndex = index
                                                        showImageViewer = true
                                                    }
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        
                        // Control buttons area
                        if (showAnswer) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth(),
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                // Primary button: Mark as Correct
                                Button(
                                    onClick = {
                                        showSuccessAnimation = true
                                        scope.launch {
                                            successState.reset()
                                            successState.trigger()
                                            kotlinx.coroutines.delay(600L)
                                            
                                            service.reviewCard(currentCard.id, true)
                                            showAnswer = false
                                            expandedQuestion = false
                                            val list = reviewQueue.toMutableList()
                                            list.removeAt(0)
                                            reviewQueue = list
                                            completedCount++
                                            showSuccessAnimation = false
                                        }
                                    },
                                    colors = ButtonDefaults.buttonColors(
                                        containerColor = FlashcardColors.getCorrectAnswerColor()
                                    ),
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(56.dp),
                                    shape = RoundedCornerShape(12.dp)
                                ) {
                                    Icon(Icons.Filled.CheckCircle, null, modifier = Modifier.size(22.dp))
                                    Spacer(Modifier.width(8.dp))
                                    Text("I Understand", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.SemiBold)
                                }
                                
                                // Secondary button: Mark as Incorrect
                                OutlinedButton(
                                    onClick = {
                                        service.reviewCard(currentCard.id, false)
                                        showAnswer = false
                                        expandedQuestion = false
                                        val list = reviewQueue.toMutableList()
                                        list.removeAt(0)
                                        list.add(currentCard)
                                        reviewQueue = list
                                    },
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(48.dp),
                                    shape = RoundedCornerShape(12.dp),
                                    border = BorderStroke(2.dp, MaterialTheme.colorScheme.error)
                                ) {
                                    Icon(Icons.Filled.Close, null, modifier = Modifier.size(18.dp))
                                    Spacer(Modifier.width(6.dp))
                                    Text("Need More Practice", style = MaterialTheme.typography.labelMedium)
                                }
                            }
                        } else {
                            Button(
                                onClick = { showAnswer = true },
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(56.dp),
                                shape = RoundedCornerShape(12.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.primary
                                )
                            ) {
                                Icon(Icons.Filled.VisibilityOff, null, modifier = Modifier.size(22.dp))
                                Spacer(Modifier.width(8.dp))
                                Text("Reveal Answer", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.SemiBold)
                            }
                        }
                    }
                }
                
                // Success celebration overlay
                AnimatedVisibility(
                    visible = showSuccessAnimation,
                    enter = fadeIn(),
                    exit = fadeOut()
                ) {
                    val successScale = successState.getScale()
                    val successRotation = successState.getRotation()
                    val successAlpha = successState.getAlpha()
                    
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .graphicsLayer {
                                alpha = successAlpha
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            Icons.Filled.CheckCircle,
                            contentDescription = "Success",
                            modifier = Modifier
                                .size(120.dp)
                                .graphicsLayer {
                                    scaleX = successScale
                                    scaleY = successScale
                                    rotationZ = successRotation
                                },
                            tint = FlashcardColors.getCorrectAnswerColor()
                        )
                    }
                }
            }
        }
        
        // Image Viewer Dialog
        if (showImageViewer && reviewQueue.isNotEmpty()) {
            val currentCard = reviewQueue.first()
            val displayImages = currentCard.imagePaths.takeIf { it.isNotEmpty() }
                ?: currentCard.imagePath?.let { listOf(it) }
                ?: emptyList()
            
            if (displayImages.isNotEmpty()) {
                ImageViewerDialog(
                    imagePaths = displayImages,
                    initialIndex = selectedImageIndex,
                    onDismiss = { showImageViewer = false }
                )
            }
        }
    }
}

@Composable
fun ReviewCompleteScreen(
    totalCards: Int,
    onFinish: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            Icons.Filled.CheckCircle,
            contentDescription = null,
            modifier = Modifier.size(120.dp),
            tint = FlashcardColors.getCorrectAnswerColor()
        )
        Spacer(modifier = Modifier.height(24.dp))
        Text(
            "Review Complete!",
            style = MaterialTheme.typography.headlineLarge,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center
        )
        Spacer(modifier = Modifier.height(16.dp))
        Text(
            "You reviewed $totalCards cards",
            style = MaterialTheme.typography.titleMedium,
            textAlign = TextAlign.Center,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
        )
        Spacer(modifier = Modifier.height(48.dp))
        Button(
            onClick = onFinish,
            modifier = Modifier.size(width = 200.dp, height = 56.dp)
        ) {
            Text("Done", style = MaterialTheme.typography.titleMedium)
        }
    }
}
